import React, { useState, useEffect } from 'react';
import { useAgentStatus } from '../contexts/AgentStatusContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  Snackbar,
} from '@mui/material';
import Grid2 from '@mui/material/Grid';
import {
  IconArrowLeft,
  IconUserPlus,
  IconEdit,
  IconTrash,
  IconUser,
  IconPhone,
  IconEye,
  IconCheck,
  IconX,
  IconSettings,
  IconClock,
  IconBell,
  IconSearch
} from '@tabler/icons-react';

interface AgentStatus {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  color: string;
  icon: string;
  canReceiveCalls: boolean;
  isAvailable: boolean;
  isBreak: boolean;
  isOffline: boolean;
  maxDuration?: number; // Maksimum süre (dakika)
  autoLogoutAfter?: number; // Otomatik çıkış süresi (dakika)
  requiresReason: boolean; // Statü değişikliği için sebep gerekli mi
  allowedTransitions: string[]; // Bu statüden geçilebilecek statüler
  createdAt: string;
  updatedAt: string;
}

const AgentStatusManagement: React.FC = () => {
  const agentStatusContext = useAgentStatus();
  const { statuses, loading, error, fetchStatuses, createStatus, updateStatus, deleteStatus } = agentStatusContext;
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStatus, setEditingStatus] = useState<AgentStatus | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'success' | 'error' | 'warning' | 'info' });

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    color: '#4CAF50',
    icon: 'person',
    canReceiveCalls: true,
    isAvailable: true,
    isBreak: false,
    isOffline: false,
    maxDuration: 0,
    autoLogoutAfter: 0,
    requiresReason: false,
    allowedTransitions: [] as string[]
  });

  const statusIcons = [
    { value: 'person', label: 'Kişi', icon: IconUser },
    { value: 'phone', label: 'Telefon', icon: IconPhone },
    { value: 'timer', label: 'Zamanlayıcı', icon: IconClock },
    { value: 'notifications', label: 'Bildirim', icon: IconBell },
    { value: 'settings', label: 'Ayarlar', icon: IconSettings },
  ];

  const statusColors = [
    { value: '#4CAF50', label: 'Yeşil' },
    { value: '#FF9800', label: 'Turuncu' },
    { value: '#F44336', label: 'Kırmızı' },
    { value: '#2196F3', label: 'Mavi' },
    { value: '#9C27B0', label: 'Mor' },
    { value: '#607D8B', label: 'Gri' },
  ];

  useEffect(() => {
    fetchStatuses();
  }, []);

  const filteredStatuses = statuses.filter(status => {
    const matchesSearch = status.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         status.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         status.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesFilter = true;
    if (statusFilter === 'available') matchesFilter = status.isAvailable;
    else if (statusFilter === 'break') matchesFilter = status.isBreak;
    else if (statusFilter === 'offline') matchesFilter = status.isOffline;
    else if (statusFilter === 'calls') matchesFilter = status.canReceiveCalls;
    
    return matchesSearch && matchesFilter;
  });

  const handleOpenDialog = (status?: AgentStatus) => {
    if (status) {
      setEditingStatus(status);
      setFormData({
        name: status.name,
        displayName: status.displayName,
        description: status.description,
        color: status.color,
        icon: status.icon,
        canReceiveCalls: status.canReceiveCalls,
        isAvailable: status.isAvailable,
        isBreak: status.isBreak,
        isOffline: status.isOffline,
        maxDuration: status.maxDuration || 0,
        autoLogoutAfter: status.autoLogoutAfter || 0,
        requiresReason: status.requiresReason,
        allowedTransitions: status.allowedTransitions || []
      });
    } else {
      setEditingStatus(null);
      setFormData({
        name: '',
        displayName: '',
        description: '',
        color: '#4CAF50',
        icon: 'person',
        canReceiveCalls: true,
        isAvailable: true,
        isBreak: false,
        isOffline: false,
        maxDuration: 0,
        autoLogoutAfter: 0,
        requiresReason: false,
        allowedTransitions: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStatus(null);
  };

  const handleSave = async () => {
    try {
      if (editingStatus) {
        // Update existing status
        await updateStatus(editingStatus._id, formData);
        setSnackbar({
          open: true,
          message: 'Statü başarıyla güncellendi',
          severity: 'success'
        });
      } else {
        // Create new status
        await createStatus(formData);
        setSnackbar({
          open: true,
          message: 'Yeni statü başarıyla oluşturuldu',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Statü kaydetme hatası:', error);
      setSnackbar({
        open: true,
        message: 'Statü kaydedilirken hata oluştu',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (statusId: string) => {
    if (window.confirm('Bu statüyü silmek istediğinizden emin misiniz?')) {
      try {
        await deleteStatus(statusId);
        setSnackbar({
          open: true,
          message: 'Statü başarıyla silindi',
          severity: 'success'
        });
      } catch (error) {
        console.error('Statü silme hatası:', error);
        setSnackbar({
          open: true,
          message: 'Statü silinirken hata oluştu',
          severity: 'error'
        });
      }
    }
  };

  const getStatusIcon = (iconName: string) => {
    const iconConfig = statusIcons.find(icon => icon.value === iconName);
    return iconConfig ? iconConfig.icon : IconUser;
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundImage: 'url(./bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      p: 3
    }}>
      {/* Geri İkonu */}
      <Box sx={{ mb: 3 }}>
        <Box 
          sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            p: 1,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'translateX(-2px)'
            }
          }}
          onClick={() => window.history.back()}
        >
          <IconArrowLeft size={24} color="white" />
        </Box>
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ px: 10 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'white', fontFamily: 'Monaco, monospace', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
          Agent Statü Yönetimi
        </Typography>
        
        <Typography variant="body1" color="white" sx={{ mb: 3, fontFamily: 'Monaco, monospace', opacity: 0.9, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
          Agent statülerini yönetin, yeni statüler ekleyin ve mevcut statüleri düzenleyin.
        </Typography>

        {/* Filters and Actions */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid #E0E0E0', background: 'white' }}>
          <CardContent>
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  placeholder="Statü ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <IconSearch size={20} color="#666" style={{ marginRight: 8 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: 'Monaco, monospace'
                    }
                  }}
                />
              </Grid2>
            
            <Grid2 size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Filtrele</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Filtrele"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="available">Müsait</MenuItem>
                  <MenuItem value="break">Mola</MenuItem>
                  <MenuItem value="offline">Çevrimdışı</MenuItem>
                  <MenuItem value="calls">Çağrı Alabilir</MenuItem>
                </Select>
              </FormControl>
            </Grid2>
            
            <Grid2 size={{ xs: 12, md: 5 }}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<IconUserPlus size={20} />}
                  onClick={() => handleOpenDialog()}
                  sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#45a049' } }}
                >
                  Yeni Statü
                </Button>
              </Box>
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>

      {/* Status Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                  <TableCell><strong>Statü</strong></TableCell>
                  <TableCell><strong>Görünüm</strong></TableCell>
                  <TableCell><strong>Özellikler</strong></TableCell>
                  <TableCell><strong>Süre</strong></TableCell>
                  <TableCell><strong>Geçişler</strong></TableCell>
                  <TableCell><strong>İşlemler</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStatuses.map((status) => {
                  const IconComponent = getStatusIcon(status.icon);
                  return (
                    <TableRow key={status._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconComponent size={20} color={status.color} />
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {status.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {status.name}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={status.displayName}
                            sx={{ bgcolor: status.color, color: 'white' }}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {status.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {status.canReceiveCalls && (
                            <Chip label="Çağrı Alabilir" color="success" size="small" />
                          )}
                          {status.isAvailable && (
                            <Chip label="Müsait" color="info" size="small" />
                          )}
                          {status.isBreak && (
                            <Chip label="Mola" color="warning" size="small" />
                          )}
                          {status.isOffline && (
                            <Chip label="Çevrimdışı" color="default" size="small" />
                          )}
                          {status.requiresReason && (
                            <Chip label="Sebep Gerekli" color="secondary" size="small" />
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box>
                          {status.maxDuration && status.maxDuration > 0 && (
                            <Typography variant="caption" display="block">
                              Max: {status.maxDuration} dk
                            </Typography>
                          )}
                          {status.autoLogoutAfter && status.autoLogoutAfter > 0 && (
                            <Typography variant="caption" display="block">
                              Otomatik: {status.autoLogoutAfter} dk
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="caption">
                          {status.allowedTransitions.length} geçiş
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(status)}
                              sx={{ color: '#1976D2' }}
                            >
                              <IconEdit size={20} />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(status._id)}
                              sx={{ color: '#F44336' }}
                            >
                              <IconTrash size={20} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Status Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStatus ? 'Statü Düzenle' : 'Yeni Statü Oluştur'}
        </DialogTitle>
        
        <DialogContent>
          <Grid2 container spacing={2} sx={{ mt: 1 }}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Statü Adı"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="online, break, busy..."
                required
              />
            </Grid2>
            
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Görünen Ad"
                value={formData.displayName}
                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder="Çevrimiçi, Mola, Meşgul..."
                required
              />
            </Grid2>
            
            <Grid2 size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Açıklama"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Statü açıklaması..."
                multiline
                rows={2}
              />
            </Grid2>
            
            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Renk</InputLabel>
                <Select
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  label="Renk"
                >
                  {statusColors.map((color) => (
                    <MenuItem key={color.value} value={color.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            bgcolor: color.value,
                            borderRadius: '50%',
                            border: '1px solid #ccc'
                          }}
                        />
                        {color.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>
            
            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>İkon</InputLabel>
                <Select
                  value={formData.icon}
                  onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                  label="İkon"
                >
                  {statusIcons.map((icon) => {
                    const IconComponent = icon.icon;
                    return (
                      <MenuItem key={icon.value} value={icon.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconComponent />
                          {icon.label}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid2>
            
            <Grid2 size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>
                Statü Özellikleri
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.canReceiveCalls}
                        onChange={(e) => setFormData(prev => ({ ...prev, canReceiveCalls: e.target.checked }))}
                      />
                    }
                    label="Çağrı Alabilir"
                  />
                </Grid2>
                
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isAvailable}
                        onChange={(e) => setFormData(prev => ({ ...prev, isAvailable: e.target.checked }))}
                      />
                    }
                    label="Müsait"
                  />
                </Grid2>
                
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isBreak}
                        onChange={(e) => setFormData(prev => ({ ...prev, isBreak: e.target.checked }))}
                      />
                    }
                    label="Mola"
                  />
                </Grid2>
                
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isOffline}
                        onChange={(e) => setFormData(prev => ({ ...prev, isOffline: e.target.checked }))}
                      />
                    }
                    label="Çevrimdışı"
                  />
                </Grid2>
                
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.requiresReason}
                        onChange={(e) => setFormData(prev => ({ ...prev, requiresReason: e.target.checked }))}
                      />
                    }
                    label="Sebep Gerekli"
                  />
                </Grid2>
              </Grid2>
            </Grid2>
            
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Maksimum Süre (dakika)"
                type="number"
                value={formData.maxDuration}
                onChange={(e) => setFormData(prev => ({ ...prev, maxDuration: parseInt(e.target.value) || 0 }))}
                helperText="0 = sınırsız"
              />
            </Grid2>
            
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Otomatik Çıkış (dakika)"
                type="number"
                value={formData.autoLogoutAfter}
                onChange={(e) => setFormData(prev => ({ ...prev, autoLogoutAfter: parseInt(e.target.value) || 0 }))}
                helperText="0 = otomatik çıkış yok"
              />
            </Grid2>
          </Grid2>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading || !formData.name || !formData.displayName}
          >
            {editingStatus ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </Box>
  );
};

export default AgentStatusManagement;
